import React, { useState, useEffect } from 'react';
import { FiX, FiAlertCircle } from 'react-icons/fi';

const RosterStudentModal = ({ student, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    'Student ID': '',
    'First Name': '',
    'Last Name': '',
    'Grade Level': '',
    'Campus/Entity': '',
    'Email Address': '',
    'Phone Number': '',
    'Country Code': ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (student) {
      setFormData({
        'Student ID': student['Student ID'] ?? '',
        'First Name': student['First Name'] || '',
        'Last Name': student['Last Name'] || '',
        'Grade Level': student['Grade Level'] ?? '',
        'Campus/Entity': student['Campus/Entity'] || '',
        'Email Address': student['Email Address'] || '',
        'Phone Number': student['Phone Number'] || '',
        'Country Code': student['Country Code'] || ''
      });
    }
  }, [student]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData['Student ID'] || !formData['First Name'] || !formData['Last Name']) {
      setError('Student ID, First Name, and Last Name are required');
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save student');
      setLoading(false);
    }
  };

  const fields = [
    { name: 'Student ID', type: 'number', required: true, disabled: !!student },
    { name: 'First Name', type: 'text', required: true },
    { name: 'Last Name', type: 'text', required: true },
    { name: 'Grade Level', type: 'number', required: false },
    { name: 'Campus/Entity', type: 'text', required: false, placeholder: 'e.g., GARLAND H S (002)' },
    { name: 'Email Address', type: 'email', required: false },
    { name: 'Phone Number', type: 'text', required: false },
    { name: 'Country Code', type: 'text', required: false }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {student ? 'Edit Student' : 'Add Student'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {fields.map((field) => (
            <div key={field.name}>
              <label htmlFor={field.name} className="block text-sm font-medium text-gray-700 mb-1">
                {field.name} {field.required && <span className="text-red-600">*</span>}
              </label>
              <input
                type={field.type}
                id={field.name}
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                disabled={field.disabled}
                required={field.required}
                placeholder={field.placeholder}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              />
              {field.disabled && (
                <p className="mt-1 text-xs text-gray-500">Student ID cannot be changed after creation</p>
              )}
            </div>
          ))}

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : student ? 'Update Student' : 'Add Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RosterStudentModal;
